import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SignaturePad } from "@/components/SignaturePad";
import { ContractDocument } from "@/components/ContractDocument";
import { STATUS_META } from "@/lib/partner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, CheckCircle2, PenLine } from "lucide-react";

export default function SignContractPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [sig, setSig] = useState(null);
  const [name, setName] = useState("");
  const [signing, setSigning] = useState(false);

  const load = async () => {
    try {
      const res = await api.get(`/partners/public/${token}`);
      setData(res.data);
      setName(res.data.partner_name || "");
    } catch { setNotFound(true); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const sign = async () => {
    if (!sig) return toast.error("Gambar tanda tangan Anda dulu");
    if (!name.trim()) return toast.error("Isi nama Anda");
    setSigning(true);
    try {
      await api.post(`/partners/public/${token}/sign`, { signature: sig, signer_name: name });
      toast.success("Terima kasih! Dokumen berhasil ditandatangani.");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSigning(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-100"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>;
  if (notFound) return <div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-500" data-testid="sign-notfound">Contract not found or link is invalid.</div>;

  const alreadySigned = !!data.partner_signature;

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="h-10 w-10 rounded-xl bg-[#1F1B16] flex items-center justify-center overflow-hidden p-1.5">
            <img src="/logo-mark.png" alt="BedsolvingOps" className="h-full w-full object-contain" />
          </div>
          <div>
            <div className="font-display font-bold text-slate-900">BedsolvingOps</div>
            <div className="text-[11px] text-slate-500">Seamless Workflow &amp; Transparent Yield</div>
          </div>
          <Badge className={`ml-auto ${STATUS_META[data.status]?.cls}`} data-testid="sign-status">{STATUS_META[data.status]?.label}</Badge>
        </div>

        <Tabs defaultValue="agreement">
          <TabsList className="mb-3">
            <TabsTrigger value="agreement">Partnership Agreement</TabsTrigger>
            <TabsTrigger value="authorization">Letter of Authorization</TabsTrigger>
          </TabsList>
          <TabsContent value="agreement"><ContractDocument type="agreement" data={data} /></TabsContent>
          <TabsContent value="authorization"><ContractDocument type="authorization" data={data} /></TabsContent>
        </Tabs>

        <div className="mt-6 bg-white rounded-xl border border-slate-200 p-5">
          {alreadySigned ? (
            <div className="flex items-center gap-2 text-emerald-600" data-testid="sign-done">
              <CheckCircle2 className="h-5 w-5" /> Dokumen sudah Anda tandatangani pada {new Date(data.partner_signed_at).toLocaleDateString("en-GB")}. Terima kasih!
            </div>
          ) : (
            <>
              <p className="text-sm font-medium mb-3 flex items-center gap-1.5 text-slate-800"><PenLine className="h-4 w-4" /> Tanda tangan Anda (Party 2)</p>
              <div className="space-y-1.5 mb-3">
                <Label>Nama lengkap</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="signer-name-input" />
              </div>
              <SignaturePad onChange={setSig} testId="partner-signature-pad" />
              <Button className="mt-3 w-full bg-indigo-600 hover:bg-indigo-700" onClick={sign} disabled={signing} data-testid="partner-sign-btn">
                {signing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Setuju & tanda tangani"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
